// src/components/draft/DraftBoard.js - Draft Board Visualization
import React, { useMemo } from 'react';
import { Users, Trophy, AlertTriangle, User, CheckCircle, Clock } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import PlayerLink from '../PlayerLink';

const DraftBoard = ({ draftState, currentTurnInfo }) => {
  // ========================================
  // GRID MAP — resolved_pick_grid is the source of truth
  // ========================================
  const gridMap = useMemo(() => {
    const map = {};
    (draftState?.resolved_pick_grid || []).forEach(entry => {
      const key = `${entry.round_number}_${entry.pick_in_round}`;
      map[key] = entry;
    });
    return map;
  }, [draftState]);

  // ========================================
  // ORGANIZE PICKS BY ROUND
  // ========================================
  const picksByRound = useMemo(() => {
    if (!draftState || !draftState.picks) return {};

    const organized = {};

    draftState.picks.forEach(pick => {
      const round = pick.round_number;
      if (!organized[round]) {
        organized[round] = [];
      }
      organized[round].push(pick);
    });

    // Sort each round by pick_in_round (backend field name)
    Object.keys(organized).forEach(round => {
      organized[round].sort((a, b) => a.pick_in_round - b.pick_in_round);
    });

    return organized;
  }, [draftState]);

  // ========================================
  // RENDER PICK CARD
  // ========================================
  const renderPick = (pick, gridEntry) => {
    const isPhantom = pick.is_phantom || false;
    const isTraded = gridEntry?.is_traded;

    return (
      <div
        key={pick.pick_id}
        className={`${dynastyTheme.components.card.base} p-4 ${
        isPhantom ? `border-2 ${dynastyTheme.classes.border.primaryBright}` : ''
        }`}
      >
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className={`text-sm font-bold ${dynastyTheme.classes.text.primary}`}>
              #{pick.overall_pick}
            </span>
            {isPhantom && (
              <AlertTriangle className={`w-4 h-4 ${dynastyTheme.classes.text.warning}`} title="Phantom Player" />
            )}
          </div>
          <CheckCircle className={`w-4 h-4 ${dynastyTheme.classes.text.success}`} />
        </div>

        <div className={`font-bold ${dynastyTheme.classes.text.white} mb-1`}>
          <PlayerLink mlbId={pick.player_id || pick.player?.ids?.mlb || null} name={pick.player_name || pick.player?.info?.full_name || 'Unknown'} style={{ fontWeight: 700, color: '#f9fafb' }} />
          {isPhantom && (
            <span className={`ml-2 text-xs ${dynastyTheme.classes.text.warning}`}>
              ⚠️
            </span>
          )}
        </div>

        <div className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
          {pick.team_name || 'Unknown Team'}
          {isTraded && gridEntry?.original_owner_name && (
            <span className={`block text-xs ${dynastyTheme.classes.text.neutralDark} italic`}>
              (via {gridEntry.original_owner_name})
            </span>
          )}
        </div>

        {pick.picked_at && (
          <div className={`text-xs ${dynastyTheme.classes.text.neutralDark} mt-2`}>
            {new Date(pick.picked_at).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        )}
      </div>
    );
  };

  // ========================================
  // RENDER EMPTY SLOT
  // ========================================
  const renderEmptySlot = (round, pickInRound, overallPick, gridEntry) => {
    const isCurrent = draftState?.draft_status === 'in_progress' &&
                      draftState?.current_round === round &&
                      draftState?.current_pick_in_round === pickInRound;
    const teamName = gridEntry?.team_name || 'TBD';
    const isTraded = gridEntry?.is_traded;

    return (
      <div
        key={`empty-${round}-${pickInRound}`}
        className={`${dynastyTheme.components.card.base} p-4 border-2 ${
          isCurrent ? `${dynastyTheme.classes.border.primaryBright} animate-pulse` : 'border-dashed ' + dynastyTheme.classes.border.neutral
        }`}
      >
        <div className="flex items-start justify-between mb-2">
          <span className={`text-sm font-bold ${dynastyTheme.classes.text.neutralDark}`}>
            #{overallPick}
          </span>
          {isCurrent && (
            <Clock className={`w-4 h-4 ${dynastyTheme.classes.text.primary} animate-pulse`} />
          )}
        </div>

        <div className={`font-bold ${isCurrent ? dynastyTheme.classes.text.primary : dynastyTheme.classes.text.neutralDark} mb-1`}>
          {isCurrent ? 'On the Clock' : 'Upcoming Pick'}
        </div>

        <div className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
          {teamName}
          {isTraded && gridEntry?.original_owner_name && (
            <span className={`block text-xs ${dynastyTheme.classes.text.neutralDark} italic`}>
              (via {gridEntry.original_owner_name})
            </span>
          )}
        </div>
      </div>
    );
  };

  // ========================================
  // RENDER ROUND
  // ========================================
  const renderRound = (roundNumber) => {
    const picks = picksByRound[roundNumber] || [];
    const totalTeams = draftState?.draft_order?.length || 0;

    // Create slots for all picks in this round
    const slots = [];
    for (let i = 1; i <= totalTeams; i++) {
      const pick = picks.find(p => p.pick_in_round === i);
      const overallPick = (roundNumber - 1) * totalTeams + i;
      const gridEntry = gridMap[`${roundNumber}_${i}`];

      if (pick) {
        slots.push(renderPick(pick, gridEntry));
      } else {
        slots.push(renderEmptySlot(roundNumber, i, overallPick, gridEntry));
      }
    }

    return (
      <div key={roundNumber} className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <Trophy className={`w-6 h-6 ${dynastyTheme.classes.text.primary}`} />
          <h3 className={`text-2xl font-bold ${dynastyTheme.classes.text.white}`}>
            Round {roundNumber}
          </h3>
          <span className={`${dynastyTheme.classes.text.neutralLight} text-sm`}>
            {picks.length} / {totalTeams} picks made
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {slots}
        </div>
      </div>
    );
  };

  // ========================================
  // DRAFT ORDER BANNER — show current round's resolved order
  // ========================================
  const activeRound = draftState?.current_round || 1;
  const draftOrderDisplay = useMemo(() => {
    const grid = draftState?.resolved_pick_grid || [];
    const roundEntries = grid
      .filter(e => e.round_number === activeRound)
      .sort((a, b) => a.pick_in_round - b.pick_in_round);
    // Fall back to natural draft_order if grid isn't populated yet
    if (roundEntries.length > 0) return roundEntries;
    return (draftState?.draft_order || []).map((t, i) => ({
      team_id: t.team_id,
      team_name: t.team_name,
      pick_in_round: i + 1,
      is_traded: false,
      original_owner_name: null
    }));
  }, [draftState, activeRound]);

  // ========================================
  // MAIN RENDER
  // ========================================
  if (!draftState) {
    return (
      <div className="text-center py-12">
        <User className={`w-12 h-12 mx-auto mb-4 ${dynastyTheme.classes.text.neutralDark}`} />
        <p className={dynastyTheme.classes.text.neutralLight}>
          No draft data available
        </p>
      </div>
    );
  }

  const totalRounds = draftState.total_rounds || 2;

  return (
    <div className="space-y-8">
      {/* DRAFT ORDER SUMMARY */}
      <div className={`${dynastyTheme.components.card.base} p-4`}>
        <h4 className={`text-sm font-bold ${dynastyTheme.classes.text.neutralLight} mb-3`}>
          Draft Order — Round {activeRound}
        </h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {draftOrderDisplay.map((entry, index) => (
            <div
              key={entry.team_id || index}
              className={`px-3 py-2 rounded ${dynastyTheme.classes.bg.darkLighter} text-sm`}
            >
              <span className={`${dynastyTheme.classes.text.primary} font-bold mr-2`}>
                {entry.pick_in_round || index + 1}.
              </span>
              <span className={dynastyTheme.classes.text.white}>
                {entry.team_name}
              </span>
              {entry.is_traded && entry.original_owner_name && (
                <span className={`block text-xs ${dynastyTheme.classes.text.neutralDark} italic`}>
                  (via {entry.original_owner_name})
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* PHANTOM PICKS WARNING */}
      {draftState.phantom_picks > 0 && (
        <div className={`${dynastyTheme.components.badge.warning} p-4 rounded-lg border-2`}>
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6" />
            <div>
              <div className="font-bold mb-1">
                {draftState.phantom_picks} Phantom Player{draftState.phantom_picks !== 1 ? 's' : ''} Drafted
              </div>
              <div className="text-sm">
                These players are not yet in the MLB database and will be automatically promoted when they appear in the API.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ROUNDS */}
      {Array.from({ length: totalRounds }, (_, i) => i + 1).map(roundNumber =>
        renderRound(roundNumber)
      )}

      {/* NO PICKS YET */}
      {draftState.total_picks === 0 && (
        <div className="text-center py-12">
          <Users className={`w-16 h-16 mx-auto mb-4 ${dynastyTheme.classes.text.neutralDark}`} />
          <p className={`text-lg ${dynastyTheme.classes.text.neutralLight}`}>
            No picks made yet. Draft is ready to begin!
          </p>
        </div>
      )}
    </div>
  );
};

export default DraftBoard;
